import { type ReactElement, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button, toast } from "@olivahealth/oli-ui";
import type { ResetUserMutation } from "./__generated__/ResetUserMutation.graphql";
import UserSelector from "./UserSelector";

const ResetUserMutation = graphql`
  mutation ResetUserMutation($userId: ID!) {
    adminResetUser(userId: $userId) {
      __typename
      ... on AdminUserReset {
        success
      }
      ... on ForbiddenError {
        reason
      }
    }
  }
`;

export default function ResetUser(): ReactElement {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [mutate, isResetting] =
    useMutation<ResetUserMutation>(ResetUserMutation);

  const handleResetUser = () => {
    if (!selectedUserId) return;

    mutate({
      variables: {
        userId: selectedUserId,
      },
      onCompleted: (response) => {
        if (response.adminResetUser.__typename === "AdminUserReset") {
          toast({
            body: "User has been reset successfully",
            variant: "success",
          });
          setSelectedUserId("");
        } else if (response.adminResetUser.__typename === "ForbiddenError") {
          toast({ body: response.adminResetUser.reason, variant: "error" });
        }
      },
      onError: (error) => {
        toast({ body: error.message, variant: "error" });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">Reset User</h3>
      <div className="flex gap-4">
        <div className="flex-1">
          <UserSelector
            onUserSelected={(user) => setSelectedUserId(user.id)}
            placeholder="Search for a user..."
          />
        </div>
        <Button
          disabled={!selectedUserId || isResetting}
          onClick={handleResetUser}
          variant="danger"
        >
          {isResetting ? "Resetting..." : "Reset User"}
        </Button>
      </div>
    </div>
  );
}
